import { StrictMode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { MsalProvider } from "@azure/msal-react";
import { PublicClientApplication, type IPublicClientApplication } from "@azure/msal-browser";
import { PostHogProvider } from '@posthog/react'

import "./index.css";
import "highlight.js/styles/github-dark.css";
import { queryClient } from "./configs/queryClient";
import { router } from "./configs/router/router";
import AuthProvider, { useAuth } from "./auth/authProvider";
import TokenAuthProvider from "./auth/tokenAuthProvider";
import { createMsalConfig } from "./auth/msalConfig";
import { setToken } from "./api/axios";
import type { TenantType } from "./auth/azureAdConfig";
import { FeatureFlagsProvider } from "./context/featureFlagsContext";
import { RolesProvider } from "./context/rolesContext";
import {
    TENANT_STORAGE_KEY,
    AUTH_METHOD_STORAGE_KEY as AUTH_METHOD_KEY,
    TOKEN_STORAGE_KEY,
} from "./auth/authStorageKeys";

const AuthBridge = ({ children }: { children: React.ReactNode }) => {
    const authContext = useAuth();

    useEffect(() => {
        setToken(authContext.token);
    }, [authContext.token]);

    if (authContext.isLoading) {
        return;
    }

    if (!authContext.token) {
        return;
    }

    setToken(authContext.token);

    return <>{children}</>;
};

const App = () => {
    const [msalInstance, setMsalInstance] = useState<IPublicClientApplication | null>(null);
    const [isInitializing, setIsInitializing] = useState(true);
    const [authMethod, setAuthMethod] = useState<"azure" | "token" | null>(null);

    useEffect(() => {
        const initializeMsal = async () => {
            const storedAuthMethod = localStorage.getItem(AUTH_METHOD_KEY);
            if (storedAuthMethod === "token" && localStorage.getItem(TOKEN_STORAGE_KEY)) {
                setAuthMethod("token");
                setIsInitializing(false);
                return;
            }
            const storedTenant = localStorage.getItem(TENANT_STORAGE_KEY) as TenantType | null;

            if (storedTenant) {
                try {
                    const config = createMsalConfig(storedTenant);
                    const instance = new PublicClientApplication(config);

                    await instance.initialize();

                    const timeoutPromise = new Promise<"timeout">((resolve) => {
                        setTimeout(() => resolve("timeout"), 2000);
                    });

                    const result = await Promise.race([instance.handleRedirectPromise(), timeoutPromise]);

                    if (result === "timeout") {
                        const accounts = instance.getAllAccounts();
                        if (accounts.length > 0) {
                            setMsalInstance(instance);
                        } else {
                            localStorage.removeItem(TENANT_STORAGE_KEY);
                            setMsalInstance(null);
                        }
                    } else if (result && result.account) {
                        setMsalInstance(instance);
                    } else if (result === null) {
                        await new Promise((resolve) => setTimeout(resolve, 500));
                        const accounts = instance.getAllAccounts();
                        if (accounts.length > 0) {
                            setMsalInstance(instance);
                        } else {
                            localStorage.removeItem(TENANT_STORAGE_KEY);
                            setMsalInstance(null);
                        }
                    } else {
                        setMsalInstance(instance);
                    }
                } catch (error) {
                    console.error("[MSAL Init] ERROR occurred during MSAL initialization");
                    console.error("[MSAL Init] Error object:", error);
                    console.error("[MSAL Init] Error type:", typeof error);
                    console.error("[MSAL Init] Error constructor:", error?.constructor?.name);

                    localStorage.removeItem(TENANT_STORAGE_KEY);
                    console.log("[MSAL Init] Tenant cleared from localStorage");

                    const errorMessage = error instanceof Error ? error.message : String(error);
                    console.error("[MSAL Init] Error message:", errorMessage);

                    if (error instanceof Error && error.stack) {
                        console.error("[MSAL Init] Error stack:", error.stack);
                    }

                    if (errorMessage.includes("AADSTS500208")) {
                        console.error("[MSAL Init] AADSTS500208 error - Invalid account domain");
                        sessionStorage.setItem(
                            "login_error",
                            "Invalid account domain. Please use the correct login option for your account type."
                        );
                    } else {
                        console.error("[MSAL Init] Generic authentication error");
                        sessionStorage.setItem("login_error", "Authentication failed. Please try again.");
                    }
                }
            } else {
                console.log("[MSAL Init] No stored tenant found");
            }

            console.log("[MSAL Init] Initialization complete, isInitializing set to false");
            setIsInitializing(false);
        };

        initializeMsal();
    }, []);

    if (isInitializing) {
        console.log("[MSAL Render] Showing loading screen");
        return (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
                <div>Loading...</div>
            </div>
        );
    }

    if (!msalInstance) {
        if (authMethod === "token") {
            console.log("[Auth Render] Rendering with token auth provider");
            return (
                <QueryClientProvider client={queryClient}>
                    <TokenAuthProvider>
                        <FeatureFlagsProvider>
                            <RolesProvider>
                                <RouterProvider router={router} />
                            </RolesProvider>
                        </FeatureFlagsProvider>
                    </TokenAuthProvider>
                </QueryClientProvider>
            );
        }

        console.log("[MSAL Render] No MSAL instance - rendering without authentication");
        return (
            <QueryClientProvider client={queryClient}>
                <RouterProvider router={router} />
            </QueryClientProvider>
        );
    }

    console.log("[MSAL Render] Rendering with MSAL provider");
    return (
        <MsalProvider instance={msalInstance}>
            <AuthProvider>
                <AuthBridge>
                    <QueryClientProvider client={queryClient}>
                        <FeatureFlagsProvider>
                            <RolesProvider>
                                <RouterProvider router={router} />
                            </RolesProvider>
                        </FeatureFlagsProvider>
                    </QueryClientProvider>
                </AuthBridge>
            </AuthProvider>
        </MsalProvider>
    );
};

const options = {
    api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
    defaults: "2026-01-30",
} as const;

const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
    const root = createRoot(rootElement);
    root.render(
        <StrictMode>
            <PostHogProvider apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN} options={options}>
                <App />
            </PostHogProvider>
        </StrictMode>
    );
}
